import React, { useEffect, useState, useContext } from 'react';
import { FormProvider } from 'react-hook-form';
import { useCodeList } from '@hooks/index';
import { post, get } from '@utils/axios_api';
import { toast } from 'react-toastify';
import { AuthContext } from '@context';

import CodeHListSection from './CodeHListSection';
import CodeHFormSection from './CodeHFormSection';
import CodeDListSection from './CodeDListSection';
import CodeDFormSection from './CodeDFormSection';
import CodeHDetailModal from './CodeHDetailModal';
import CodeDDetailModal from './CodeDDetailModal';

const CodePage = () => {
    const { hasPermission } = useContext(AuthContext);
    const canWrite = hasPermission('CODE_WRITE');

    const {
        methods, data, isLoading, isError, error, onSubmit,
        handlePageChange, changePageSize, refetch,
        pageIndex, recordCountPerPage, initialCodeH, initialCodeD, useYnOptions
    } = useCodeList();

    const [selectCodeH, setSelectCodeH] = useState(initialCodeH);
    const [filterCodeDList, setFilterCodeDList] = useState([]);
    const [selectCodeD, setSelectCodeD] = useState(initialCodeD);
    const [isDuplH, setIsDuplH] = useState(false);
    const [isDuplD, setIsDuplD] = useState(false);
    const [duplHMsg, setDuplHMsg] = useState();
    const [duplDMsg, setDuplDMsg] = useState();
    
    const [hDetailOpen, setHDetailOpen] = useState(false);
    const [hDetailItem, setHDetailItem] = useState(null);
    const [dDetailOpen, setDDetailOpen] = useState(false);
    const [dDetailItem, setDDetailItem] = useState(null);

    useEffect(() => {
        if (data?.codeDList && selectCodeH.comhCode) {
            const filtered = data.codeDList.filter(
                item => item.comhCode === selectCodeH.comhCode);
            setFilterCodeDList(filtered);
        }
    }, [data?.codeDList, selectCodeH.comhCode]);

    const handleCodeH = (comhCode) => {
        const filter = data.codeHList.filter((item) => comhCode === item.comhCode);
        if (filter.length > 0) {
            setSelectCodeH({ ...filter[0], isNew: false, isEdited: true });
            setSelectCodeD({ ...initialCodeD, comhCode: comhCode });
        }
    };

    const handleCodeD = (comdCode) => {
        const filter = filterCodeDList.filter((item) => comdCode === item.comdCode);
        if (filter.length > 0) {
            setSelectCodeD({ ...filter[0], comhCode: selectCodeH.comhCode, isNew: false, isEdited: true });
        }
    };

    const filteredCodeD = async (comhCode) => {
        try {
            const response = await get("/admin/comm/code/codeDList", {
                params: { comhCode },
                headers: { 'Content-type': 'application/json' }
            });
            setFilterCodeDList(response.resultList || []);
        } catch (error) {
            console.error("조회 중 오류:", error);
        }
    };

    const handleHChange = (e) => {
        const { name, value } = e.target;
        setSelectCodeH((prev) => ({
            ...prev,
            [name]: value,
            code: "codeH",
            isNew: prev.isNew,
            isEdited: prev.isEdited,
        }));
    };

    const handleDChange = (e) => {
        const { name, value } = e.target;
        setSelectCodeD((prev) => ({
            ...prev,
            [name]: value,
            code: "codeD",
            isNew: prev.isNew,
            isEdited: prev.isEdited,
        }));
    };

    const handleAdd = (type) => {
        if (type === "h") {
            setSelectCodeH(initialCodeH);
            setDuplHMsg("");
        } else if (type === "d") {
            setSelectCodeD((prev) => ({ ...initialCodeD, comhCode: prev.comhCode }));
            setDuplDMsg("");
        }
    };

    const handleHSave = async () => {
        if (!selectCodeH.comhCode || !selectCodeH.comhName || !selectCodeH.hsort || !selectCodeH.huseYn) {
            toast.warn("필수 입력사항을 입력하십시오.");
            return;
        }

        if (!selectCodeH.isNew && !selectCodeH.isEdited) {
            toast.warn("저장할 사항이 없습니다.");
            return;
        }

        if (isDuplH) {
            toast.warn("그룹코드가 중복입니다.");
            return;
        }

        try {
            const comhCode = selectCodeH.comhCode;
            const url = selectCodeH.isNew ? "/admin/comm/code/register" : `/admin/comm/code/${comhCode}`

            const response = await post(url, selectCodeH, {
                headers: { 'Content-type': 'application/json' }
            });

            toast.success(response.message);
            setDuplHMsg('');
            setSelectCodeH(prev => ({ ...prev, isNew: false, isEdited: false }));
            setIsDuplH(false);
            refetch();
        } catch (error) {
            console.error("저장 중 오류:", error);
        }
    };

    const handleDSave = async () => {
        if (!selectCodeD.comdCode || !selectCodeD.comdName || !selectCodeD.dsort || !selectCodeD.duseYn) {
            toast.warn("필수 입력사항을 입력하십시오.");
            return;
        }

        if (!selectCodeD.isNew && !selectCodeD.isEdited) {
            toast.warn("저장할 사항이 없습니다.");
            return;
        }

        if (isDuplD) {
            toast.warn("공통코드가 중복입니다.");
            return;
        }

        try {
            const comhCode = selectCodeD.comhCode;
            const comdCode = selectCodeD.comdCode;
            const url = selectCodeD.isNew ? "/admin/comm/code/register" : `/admin/comm/code/${comhCode}/${comdCode}`

            const response = await post(url, selectCodeD, {
                headers: { 'Content-type': 'application/json' }
            });

            toast.success(response.message);
            setDuplDMsg('');
            setSelectCodeD(prev => ({ ...prev, isNew: false, isEdited: false }));
            setIsDuplD(false);
            await filteredCodeD(comhCode);
            refetch();
        } catch (error) {
            console.error("저장 중 오류:", error);
        }
    };

    const handleDuplH = async () => {
        if (!selectCodeH.comhCode) {
            toast.warn("그룹코드를 입력하십시오.");
            setDuplHMsg("");
            return;
        }

        try {
            const url = "/admin/comm/code/isdupl";
            const response = await post(url, selectCodeH, {
                headers: { 'Content-type': 'application/json' }
            });

            if (response.isDupl > 0) {
                setDuplHMsg("이미 존재하는 코드입니다.");
                setIsDuplH(true);
            } else {
                setDuplHMsg("사용 가능한 코드입니다.");
                setIsDuplH(false);
            }
            refetch();
        } catch (error) {
            console.log("중복확인 중 오류:", error);
        }
    };

    const handleDuplD = async () => {
        if (!selectCodeD.comdCode) {
            toast.warn("공통코드를 입력하십시오.");
            setDuplDMsg("");
            return;
        }

        try {
            const url = "/admin/comm/code/isdupl";
            const response = await post(url, selectCodeD, {
                headers: { 'Content-type': 'application/json' }
            });

            if (response.isDupl > 0) {
                setDuplDMsg("이미 존재하는 코드입니다.");
                setIsDuplD(true);
            } else {
                setDuplDMsg("사용 가능한 코드입니다.");
                setIsDuplD(false);
            }
            refetch();
        } catch (error) {
            console.log("중복확인 중 오류:", error);
        }
    };

    if (isLoading) {
        return <div>로딩 중...</div>;
    }
    if (isError) {
        console.log("isError:", error);
        return <div>오류가 발생했습니다: {error?.message || "알 수 없는 에러"}</div>;
    }

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <div className="contents-wrap !mt-4 md:!mt-5 !px-3 sm:!px-4 md:!px-6 lg:!px-8 !pt-3 md:!pt-4 !pb-4 md:!pb-6 !text-[16px] md:!text-[17px] lg:!text-[18px]">
                    <div className="page-title-wrap !mb-3 md:!mb-4 !flex-col sm:!flex-row !items-start sm:!items-center gap-2">
                        <h2 className="!text-[26px] md:!text-[30px] !font-extrabold tracking-tight flex items-center gap-2">
                            <span className="icon-dot-circle"></span>
                            공통코드 관리
                        </h2>
                    </div>

                    <div className="tab-cont-wrap">
                        <div className="tab-cont-01 on">
                            <div className="row-1 flex-col lg:flex-row min-h-0">
                                <CodeHListSection
                                    data={data}
                                    pageIndex={pageIndex}
                                    recordCountPerPage={recordCountPerPage}
                                    handlePageChange={handlePageChange}
                                    changePageSize={changePageSize}
                                    handleCodeH={handleCodeH}
                                    filteredCodeD={filteredCodeD}
                                    setHDetailItem={setHDetailItem}
                                    setHDetailOpen={setHDetailOpen}
                                />
                                <CodeHFormSection
                                    canWrite={canWrite}
                                    handleAdd={handleAdd}
                                    handleHSave={handleHSave}
                                    selectCodeH={selectCodeH}
                                    handleHChange={handleHChange}
                                    handleDuplH={handleDuplH}
                                    duplHMsg={duplHMsg}
                                    useYnOptions={useYnOptions}
                                />
                            </div>

                            <div className="row-1 flex-col lg:flex-row min-h-0">
                                <CodeDListSection
                                    filterCodeDList={filterCodeDList}
                                    data={data}
                                    pageIndex={pageIndex}
                                    handlePageChange={handlePageChange}
                                    handleCodeD={handleCodeD}
                                    setDDetailItem={setDDetailItem}
                                    setDDetailOpen={setDDetailOpen}
                                />
                                <CodeDFormSection
                                    canWrite={canWrite}
                                    handleAdd={handleAdd}
                                    handleDSave={handleDSave}
                                    selectCodeD={selectCodeD}
                                    handleDChange={handleDChange}
                                    handleDuplD={handleDuplD}
                                    duplDMsg={duplDMsg}
                                    useYnOptions={useYnOptions}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <CodeHDetailModal 
                    open={hDetailOpen} 
                    onClose={() => setHDetailOpen(false)} 
                    item={hDetailItem} 
                />
                <CodeDDetailModal 
                    open={dDetailOpen} 
                    onClose={() => setDDetailOpen(false)} 
                    item={dDetailItem} 
                />
            </form>
        </FormProvider>
    );
};

export default CodePage;
